import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { cambiarEstadoSocio, obtenerSocioPorId } from '../services/sociosService'
import EstadoSocioBadge from '../components/socios/EstadoSocioBadge'
import EstadoCursoBadge from '../components/cursos/EstadoCursoBadge'
import AsociarCursosModal from '../components/cursos/AsociarCursosModal'
import { obtenerCursosDeSocio, quitarCursoSocio } from '../services/socioCursosService'
import { obtenerAclesDeSocio } from '../services/socioAclesService'
import { Icon } from '../components/ui/Icons'
import SocioCredentialSection from '../components/socios/SocioCredentialSection'

function fullName(socio) {
  return [socio.nombres, socio.apellido_paterno, socio.apellido_materno].filter(Boolean).join(' ')
}

function formatDate(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(`${value}T00:00:00`))
}

function Detail({ label, children, wide = false }) {
  return <div className={wide ? 'sm:col-span-2' : ''}><dt className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{label}</dt><dd className="mt-1.5 whitespace-pre-wrap font-medium">{children || '—'}</dd></div>
}

export default function SocioDetallePage() {
  const { id } = useParams()
  const { organizacion } = useAuth()
  const location = useLocation()
  const [socio, setSocio] = useState(null)
  const [courses, setCourses] = useState([])
  const [acles, setAcles] = useState([])
  const [loading, setLoading] = useState(true)
  const [changing, setChanging] = useState(false)
  const [removingCourseId, setRemovingCourseId] = useState('')
  const [associationOpen, setAssociationOpen] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState(location.state?.message || '')

  useEffect(() => {
    let active = true
    Promise.all([obtenerSocioPorId(organizacion.id, id), obtenerCursosDeSocio(organizacion.id, id), obtenerAclesDeSocio(organizacion.id, id)])
      .then(([socioResult, courseResult, acleResult]) => { if (active) { setSocio(socioResult); setCourses(courseResult); setAcles(acleResult) } })
      .catch((loadError) => { if (active) setError(loadError.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [organizacion.id, id])

  async function changeStatus(nextStatus) {
    if (nextStatus === 'inactivo' && !window.confirm('¿Deseas inactivar a este socio?')) return
    setChanging(true)
    setError('')
    setMessage('')
    try {
      const updated = await cambiarEstadoSocio(organizacion.id, id, nextStatus)
      setSocio(updated)
      setMessage(nextStatus === 'activo' ? 'Socio activado correctamente.' : 'Socio inactivado correctamente.')
    } catch (changeError) {
      setError(changeError.message)
    } finally {
      setChanging(false)
    }
  }

  async function refreshCourses(savedCount = 0) {
    const result = await obtenerCursosDeSocio(organizacion.id, id)
    setCourses(result)
    setAssociationOpen(false)
    if (savedCount) setMessage(savedCount === 1 ? 'Curso asociado correctamente.' : `${savedCount} cursos asociados correctamente.`)
  }

  async function removeCourse(course) {
    if (!window.confirm('¿Deseas quitar este curso del socio?')) return
    setRemovingCourseId(course.id)
    setError('')
    setMessage('')
    try {
      await quitarCursoSocio(organizacion.id, id, course.id)
      setCourses((current) => current.filter((item) => item.id !== course.id))
      setMessage('Curso quitado correctamente.')
    } catch (removeError) {
      setError(removeError.message)
    } finally {
      setRemovingCourseId('')
    }
  }

  if (loading) return <div className="py-16 text-center text-sm text-text-secondary">Cargando socio…</div>
  if (!socio) return <div className="rounded-2xl border border-danger/25 bg-danger/5 p-6 text-danger" role="alert">{error || 'Socio no encontrado.'}</div>

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Link to="/socios" className="-ml-1 inline-flex min-h-11 items-center gap-1 px-1 text-sm font-semibold text-primary hover:text-primary-hover"><Icon name="arrowLeft" />Volver a socios</Link>
      {message && <div className="rounded-xl border border-success/30 bg-success/10 p-4 text-sm font-medium text-success-text" role="status">{message}</div>}
      {error && <div className="rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}

      <div className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-5 shadow-sm sm:flex-row sm:items-start sm:justify-between sm:p-6">
        <div className="min-w-0"><p className="text-sm font-semibold text-primary">Detalle del socio</p><h2 className="mt-1 break-words text-2xl font-bold tracking-tight sm:text-3xl">{fullName(socio)}</h2><div className="mt-3 flex flex-wrap items-center gap-3"><EstadoSocioBadge estado={socio.estado} />{socio.numero_socio && <span className="text-sm text-text-secondary">N.º {socio.numero_socio}</span>}</div></div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link to={`/socios/${socio.id}/editar`} className="inline-flex items-center justify-center gap-2 min-h-11 rounded-xl border border-secondary px-4 py-2.5 font-semibold text-primary transition hover:bg-secondary/5"><Icon name="edit" />Editar</Link>
          {socio.estado === 'inactivo' ? <button type="button" onClick={() => changeStatus('activo')} disabled={changing} className="min-h-11 rounded-xl bg-success px-4 py-2.5 font-semibold text-white transition hover:brightness-95 disabled:opacity-60">{changing ? 'Actualizando…' : 'Activar socio'}</button> : <button type="button" onClick={() => changeStatus('inactivo')} disabled={changing} className="min-h-11 rounded-xl bg-danger px-4 py-2.5 font-semibold text-white transition hover:brightness-95 disabled:opacity-60">{changing ? 'Actualizando…' : 'Inactivar socio'}</button>}
        </div>
      </div>

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-sm sm:p-6">
        <h3 className="text-lg font-semibold">Información del socio</h3>
        <dl className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-2">
          <Detail label="RUT">{socio.rut}</Detail>
          <Detail label="Número de socio">{socio.numero_socio}</Detail>
          <Detail label="Fecha de ingreso">{formatDate(socio.fecha_ingreso)}</Detail>
          <Detail label="Estado"><EstadoSocioBadge estado={socio.estado} /></Detail>
          <Detail label="Email">{socio.email}</Detail>
          <Detail label="Teléfono">{socio.telefono}</Detail>
          <Detail label="Dirección">{socio.direccion}</Detail>
          <Detail label="Comuna">{socio.comuna}</Detail>
          <Detail label="Observaciones" wide>{socio.observaciones}</Detail>
        </dl>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-sm sm:p-6">
        <h3 className="text-lg font-semibold">ACLE registradas</h3>
        {acles.length ? <ul className="mt-4 flex flex-wrap gap-2">{acles.map((acle) => <li key={acle.id} className="rounded-full bg-secondary/10 px-3 py-1.5 text-sm font-semibold text-primary">{acle.nombre}</li>)}</ul> : <p className="mt-2 text-sm text-text-secondary">Sin actividades ACLE registradas.</p>}
      </section>

      <section className="rounded-2xl border border-border bg-surface shadow-sm">
        <div className="flex flex-col gap-3 border-b border-border p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"><div><h3 className="text-lg font-semibold">Cursos asociados</h3><p className="mt-1 text-sm text-text-secondary">{courses.length === 1 ? '1 curso asociado' : `${courses.length} cursos asociados`}</p></div><button type="button" onClick={() => setAssociationOpen(true)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-semibold text-white transition hover:bg-primary-hover"><Icon name="plus" />Asociar curso</button></div>
        {courses.length === 0 ? <div className="px-5 py-12 text-center"><div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-secondary/10 text-primary"><Icon name="course" /></div><p className="mt-4 font-medium">Sin cursos asociados</p><p className="mt-1 text-sm text-text-secondary">Puedes asociar uno o varios cursos activos.</p></div> : <div className="divide-y divide-border">{courses.map((course) => <article key={course.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h4 className="font-semibold">{course.nombre}</h4><EstadoCursoBadge activo={course.activo} /></div><p className="mt-1 text-sm text-text-secondary">{[course.nivel, course.seccion, course.anio].filter(Boolean).join(' · ')}</p></div><button type="button" onClick={() => removeCourse(course)} disabled={removingCourseId === course.id} className="min-h-11 shrink-0 rounded-xl border border-danger/30 px-4 py-2 text-sm font-semibold text-danger transition hover:bg-danger/5 disabled:opacity-50">{removingCourseId === course.id ? 'Quitando…' : 'Quitar asociación'}</button></article>)}</div>}
      </section>

      <SocioCredentialSection organizacionId={organizacion.id} socioId={id} socioEstado={socio.estado} nombre={fullName(socio)} />

      {associationOpen && <AsociarCursosModal organizacionId={organizacion.id} socioId={id} associatedIds={courses.map((course) => course.id)} onClose={() => setAssociationOpen(false)} onSaved={refreshCourses} />}
    </div>
  )
}
